import React from 'react';
import { IonButton, IonPopover, IonContent, IonModal, IonList, IonItem, IonHeader, IonButtons, IonBackButton, IonPage, IonIcon, IonToolbar, IonGrid, IonRow, IonCol, IonCardTitle, IonLabel, IonInput, IonCard, IonCardHeader, IonCardContent, IonTitle, IonListHeader, IonTextarea, IonSelect, IonSelectOption, IonDatetime, useIonLoading, IonLoading, useIonActionSheet, IonAvatar, IonCheckbox, IonInfiniteScroll, IonInfiniteScrollContent, IonText } from '@ionic/react';
import './Announcements.css';
import { arrowBack, addCircleOutline, addCircleSharp, trashOutline, trashSharp, calendar } from "ionicons/icons";
import { useMutation, useQuery } from "@apollo/client";
import dayjs from 'dayjs';
import * as relativeTime from 'dayjs/plugin/relativeTime';
import { ANN_GET_QUERY, PAGE_SIZE, ANN_CREATE_QUERY, ANN_REMOVE_QUERY, DATE_FORMAT_UNIVERSAL } from '../schemas/announcements';
import UploadFile, { SingleUploadI } from '../components/UploadFile';
import useAuth from '../hooks/useAuth';
//@ts-ignore
dayjs.extend(relativeTime);

type Attachment = {
    filename: string;
    url: string;
    label?: string;
}

const Announcements: React.FC<any> = () => {
    const [present, _dismiss] = useIonActionSheet();
    const { loading, error, data, fetchMore } = useQuery(ANN_GET_QUERY, {
        variables: { take: PAGE_SIZE, skip: 0 }
    });
    const [createMutation, { loading: loading_create }] = useMutation(ANN_CREATE_QUERY, {
        refetchQueries: [
            ANN_GET_QUERY,
            "Announcements"
        ],
    });
    const [removeMutation, { loading: loading_remove }] = useMutation(ANN_REMOVE_QUERY, {
        refetchQueries: [
            ANN_GET_QUERY,
            "Announcements"
        ],
    });
    const [createOpen, setCreateOpen] = React.useState(false);
    const [readOnly, setReadOnly] = React.useState(false);
    const [title, setTitle] = React.useState<string | null | undefined>();
    const [text, setText] = React.useState<string | null | undefined>();
    const [entouch, setEntouch] = React.useState(false);
    const [assist, setAssist] = React.useState(false);
    const [attachments, setAttachments] = React.useState<Attachment[]>([]);
    const [activatedAt, setActivatedAt] = React.useState<string | null | undefined>(dayjs().format(DATE_FORMAT_UNIVERSAL));
    const [isInfiniteDisabled, setInfiniteDisabled] = React.useState(false);
    const { claims } = useAuth();
    
    const createAnnouncement = async () => {
        setInfiniteDisabled(false)
        await createMutation({
            variables: { 
                data: { 
                    entouch, 
                    assist, 
                    text, 
                    title, 
                    activatedAt: dayjs(activatedAt).toISOString(), 
                    attachment: attachments.map((a)=>{return {label: a.label, url: a.url}}) 
                }
            }
        })
        setCreateOpen(false)
    }

    const deleteAnnouncement = async (key: number) => {
        present({
            header: 'Are you sure you want to delete the Announcement?',
            buttons: [
                {
                    text: 'Delete',
                    role: 'destructive'
                },
                {
                    text: 'Cancel',
                    role: 'cancel'
                }
            ],
            onDidDismiss: async (ev: CustomEvent) => {
                const role = ev.detail.role;
                if (role === 'destructive') {
                    setInfiniteDisabled(false)
                    await removeMutation({ variables: { id: key } })
                    return true;
                }
                return false;
            }
        });
    }

    const openReadModal = (item: any) => {
        setReadOnly(true)
        setActivatedAt(dayjs(item.activatedAt).format(DATE_FORMAT_UNIVERSAL))
        setEntouch(item.entouch)
        setAssist(item.assist)
        setText(item.text)
        setTitle(item.title)
        setCreateOpen(true)
        setAttachments(item.attachment || [])
    }

    const openCreateModal = () => {
        setEntouch(false)
        setAssist(false)
        setText("")
        setActivatedAt(dayjs().format(DATE_FORMAT_UNIVERSAL))
        setTitle("")
        setReadOnly(false)
        setCreateOpen(true)
        setAttachments([])
    }

    const loadData = async (ev: any) => {
        let ret = await fetchMore({ variables: { skip: data.announcements.length } })
        if (ret.data.announcements.length < PAGE_SIZE) setInfiniteDisabled(true)
        ev.target.complete()
    }

    const addAttachment = ({ filename, url }: SingleUploadI) => {
        setAttachments([{ filename, url, label: "" }, ...attachments])
    }

    const updateLabel = (val: string | null | undefined, key: number) => {
        let atts = [...attachments]
        let item = {
            ...attachments[key],
            label: val || ""
        }
        atts[key] = item
        setAttachments(atts)
    }

    return (
        <IonPage>
            <IonLoading
                isOpen={loading || loading_create || loading_remove}
                message={'Please wait...'}
            />
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="secondary">
                        <IonBackButton icon={arrowBack} text="" className="custom-back" />
                    </IonButtons>
                    <IonTitle>Announcements</IonTitle>
                    {claims.rules.includes('CanAdminAnnouncements') && <IonButtons slot="end">
                        <IonButton type='button' onClick={() => openCreateModal()}>
                            <IonIcon slot="icon-only" ios={addCircleOutline} md={addCircleSharp} />
                        </IonButton>
                    </IonButtons>}
                </IonToolbar>
            </IonHeader>
            <IonModal isOpen={createOpen}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>Create Announcement</IonTitle>
                        <IonButtons slot="secondary">
                            <IonButton onClick={() => setCreateOpen(false)} slot="primary">Close</IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent>
                    <IonGrid style={{ height: '100%', display: 'grid' }}>
                        <IonRow>
                            <IonCol>
                                <IonList>
                                    <IonItem key="entouch">
                                        <IonLabel>EnTouch</IonLabel>
                                        <IonCheckbox disabled={readOnly} checked={entouch} onIonChange={e => setEntouch(e.detail.checked)} />
                                    </IonItem>
                                    <IonItem key="assist">
                                        <IonLabel>Assist</IonLabel>
                                        <IonCheckbox disabled={readOnly} checked={assist} onIonChange={e => setAssist(e.detail.checked)} />
                                    </IonItem>
                                    <IonItem key="title">
                                        <IonLabel>Title</IonLabel>
                                        <IonInput readonly={readOnly} value={title} onIonChange={(e) => setTitle(e?.detail?.value)} type='text' placeholder='Announcement title (not required)' />
                                    </IonItem>
                                    <IonItem key="text">
                                        <IonLabel>Text *</IonLabel>
                                        <IonTextarea autoGrow rows={5} readonly={readOnly} value={text} onIonChange={(e) => setText(e?.detail?.value)} required placeholder='Announcement text' />
                                    </IonItem>
                                    <IonItem key="from" id="date-input">
                                        <IonLabel>Show From</IonLabel>
                                        <IonText>{activatedAt}</IonText>

                                    </IonItem>
                                    {(!readOnly || (readOnly && attachments && attachments.length > 0)) &&<IonItem key="atts">
                                        <h2>Attachments</h2>
                                    </IonItem>}
                                    {attachments.map((att, key) =>
                                        <IonItem key={key}>
                                            {!readOnly && <IonLabel>{att.filename}</IonLabel>}
                                            {readOnly && <><a href={att.url} target="_blank">Open</a>&nbsp;</>}
                                            <IonInput readonly={readOnly} value={att.label} placeholder="Attachment Label" onIonChange={(e) => updateLabel(e?.detail?.value, key)}/>
                                        </IonItem>
                                    )}
                                    {!readOnly && <IonItem key="upload"><UploadFile callback={addAttachment} /></IonItem>}
                                </IonList>
                            </IonCol>
                        </IonRow>
                        {!readOnly && <IonRow class="ion-align-self-end">
                            <IonCol>
                                <IonButton expand="full" disabled={!text || (!assist && !entouch)} onClick={() => createAnnouncement()}>
                                    Create Announcement
                                </IonButton>
                            </IonCol>
                        </IonRow>}
                    </IonGrid>
                </IonContent>
            </IonModal>
            <IonContent fullscreen>
                <IonGrid className="ion-padding">
                    <IonRow className="ion-justify-content-center">
                        <IonCol>
                            <div>
                                <IonList>
                                    {data?.announcements?.map((item: any, index: number) => {
                                        return (
                                            <IonItem key={index}>
                                                <IonAvatar slot='start'>
                                                    <img src={"assets/" + (item.assist ? item.entouch ? "both" : "assist" : "entouch") + ".png"} />
                                                </IonAvatar>
                                                <IonLabel style={{ cursor: 'pointer' }} onClick={() => openReadModal(item)}>
                                                    <p>
                                                        {dayjs().isBefore(dayjs(item.activatedAt)) && <span>Will become active <span title={item.activatedAt}>{dayjs(item.activatedAt).fromNow()}</span></span>}
                                                        {!dayjs().isBefore(dayjs(item.activatedAt)) && <span>Activated since <span title={item.activatedAt}>{dayjs(item.activatedAt).fromNow()}</span></span>}
                                                    </p>
                                                    <h2>
                                                        {item.title}
                                                    </h2>
                                                    <p>{item.text}</p>
                                                    <p>Created <span title={item.createdAt}>{dayjs(item.createdAt).fromNow()}</span> by {item.createdBy.name}</p>
                                                </IonLabel>
                                                {claims.rules.includes('CanAdminAnnouncements') && <IonButton slot='end' onClick={() => { deleteAnnouncement(item.id) }}>
                                                    <IonIcon slot="icon-only" ios={trashOutline} md={trashSharp} />
                                                </IonButton>}
                                            </IonItem>
                                        )
                                    })}
                                </IonList>
                                <IonInfiniteScroll
                                    onIonInfinite={loadData}
                                    threshold="100px"
                                    disabled={isInfiniteDisabled}
                                >
                                    <IonInfiniteScrollContent
                                        loadingSpinner="bubbles"
                                        loadingText="Loading more data..."
                                    ></IonInfiniteScrollContent>
                                </IonInfiniteScroll>
                            </div>
                        </IonCol>
                    </IonRow>
                </IonGrid>
            </IonContent>
        </IonPage>
    );
};

export default Announcements;